/**
 * StudioCanvas.tsx — Canvas visual unificado do Studio
 *
 * O documento final e a estrutura editável ocupam a mesma superfície. Cada
 * bloco de primeiro nível usa o componente oficial do renderizador web e recebe
 * somente uma camada de seleção do editor, que nunca faz parte da publicação.
 *
 * RELEASE: v2.2.0 - 2026-09-11 - Canvas passa a usar o renderizador canônico
 *          de nós e aplica tema, largura e destaque do próprio documento.
 * RELEASE: v2.1.0 - 2026-09-10 - Workspace claro e documento com sombra suave.
 * RELEASE: v2.0.0 - 2026-09-10 - Canvas visual único, seleção contextual e
 *          pontos de inserção sobre a prévia oficial.
 * RELEASE: v1.1.0 - 2026-09-10 - Pontos de inserção entre blocos.
 * RELEASE: v1.0.0 - 2026-09-09 - Versão inicial (Studio, Fase 4).
 */

import React from 'react';
import { variaveisDeTipografia } from '@/lib/studio/core/typography';
import { ArrowDown, ArrowUp, Copy, GripVertical, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { getBlock, type StudioDocument } from '@/lib/studio/core';
import { StudioWebProvider } from '@/lib/studio/render/web/StudioWebContext';
import { StudioWebNode } from '@/lib/studio/render/web/StudioWebRenderer';
import type { PublicProposalData } from '@/hooks/usePublicProposal';

interface StudioCanvasProps {
  document: StudioDocument;
  selectedId: string | null;
  device: 'desktop' | 'mobile';
  onSelect: (id: string) => void;
  onMove: (id: string, direcao: -1 | 1) => void;
  onDuplicate: (id: string) => void;
  onRemove: (id: string) => void;
  /** Abre a biblioteca para inserir na posição indicada. */
  onRequestInsert?: (indice: number) => void;
  /** Posição atualmente escolhida para inserção (destaca o ponto). */
  insertIndex?: number | null;
}

/** Dados fictícios completos para os blocos comerciais dentro do editor. */
const PROPOSTA_EXEMPLO: PublicProposalData = {
  proposal: {
    id: 'preview',
    proposal_number: 'PRP-0001',
    version_number: 1,
    title: 'Proposta comercial de exemplo',
    description: 'Uma solução criada para o seu negócio.',
    conditions_html: '<p>Pagamento em até 30 dias. Validade de 15 dias.</p>',
    status: 'sent',
    currency: 'BRL',
    subtotal: 3200,
    discount_total: 200,
    total: 3000,
    mrr_total: 450,
    valid_from: new Date().toISOString(),
    expires_at: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString(),
    accepted_at: null,
    accepted_by_name: null,
    rejected_at: null,
    pdf_status: 'ready',
    is_actionable: true,
  },
  tenant: { name: 'Sua Empresa', logo_url: null },
  deal: { title: 'Negócio de exemplo', client_name: 'Cliente Exemplo LTDA' },
  items: [
    {
      product_name: 'Implantação da plataforma',
      product_type: 'service',
      unit_type: 'un',
      quantity: 1,
      unit_price: 2000,
      discount_percent: 0,
      discount_fixed: 0,
      subtotal: 2000,
      total: 2000,
      notes: 'Inclui treinamento da equipe.',
      recurring_price: null,
      recurrence_interval: null,
    },
    {
      product_name: 'Mensalidade do plano',
      product_type: 'service',
      unit_type: 'mês',
      quantity: 1,
      unit_price: 1200,
      discount_percent: 0,
      discount_fixed: 200,
      subtotal: 1200,
      total: 1000,
      notes: null,
      recurring_price: 450,
      recurrence_interval: 'mês',
    },
  ],
  pdf_url: null,
};

/** Botão de ferramenta com descrição acessível. */
function AcaoBloco({
  rotulo,
  disabled,
  destructive,
  onClick,
  children,
}: {
  rotulo: string;
  disabled?: boolean;
  destructive?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className={cn('h-7 w-7', destructive && 'text-destructive hover:text-destructive')}
          disabled={disabled}
          onClick={(evento) => {
            evento.stopPropagation();
            onClick();
          }}
          aria-label={rotulo}
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{rotulo}</TooltipContent>
    </Tooltip>
  );
}

/** Ponto de inserção exibido entre as seções do documento. */
function PontoDeInsercao({
  indice,
  ativo,
  onClick,
}: {
  indice: number;
  ativo: boolean;
  onClick: () => void;
}) {
  return (
    <div className="group/insert relative z-10 flex h-6 items-center justify-center">
      <div
        className={cn(
          'absolute inset-x-0 top-1/2 h-px -translate-y-1/2 transition-colors motion-reduce:transition-none',
          ativo ? 'bg-studio-accent' : 'bg-transparent group-hover/insert:bg-studio-accent/60',
        )}
      />
      <Button
        type="button"
        size="sm"
        variant={ativo ? 'default' : 'outline'}
        onClick={onClick}
        className={cn(
          'relative h-7 gap-1.5 rounded-full px-3 text-[11px] shadow-sm transition-opacity motion-reduce:transition-none',
          ativo
            ? 'bg-studio-accent text-studio-accent-foreground opacity-100 hover:bg-studio-accent/90'
            : 'opacity-0 group-hover/insert:opacity-100 focus-visible:opacity-100',
        )}
        aria-label={`Adicionar bloco na posição ${indice + 1}`}
      >
        <Plus className="h-3 w-3" />
        Adicionar bloco
      </Button>
    </div>
  );
}

export function StudioCanvas({
  document,
  selectedId,
  device,
  onSelect,
  onMove,
  onDuplicate,
  onRemove,
  onRequestInsert,
  insertIndex = null,
}: StudioCanvasProps) {
  const blocks = document.blocks;
  const tema = document.theme ?? {};
  const pagina = ((document.settings as Record<string, unknown> | undefined)?.page ?? {}) as Record<string, unknown>;
  /** Valores do documento são usados apenas como propriedades CSS, nunca como HTML. */
  const estiloDocumento = {
    backgroundColor: String(pagina.backgroundColor ?? tema.backgroundColor ?? '') || undefined,
    color: String(tema.textColor ?? '') || undefined,
    fontFamily: String(tema.fontFamily ?? '') || undefined,
    fontSize: String(tema.fontSize ?? '') || undefined,
    maxWidth: device === 'mobile' ? '390px' : String(pagina.maxWidth ?? tema.maxWidth ?? '1180px'),
    '--st-accent': String(tema.accentColor ?? '') || undefined,
    // Tipografia governada: mesmas variáveis da página publicada, para o que se
    // vê na tela de criação ser exatamente o que vai ao ar.
    ...variaveisDeTipografia(tema),
  } as React.CSSProperties;

  return (
    <div className="h-full overflow-auto bg-studio-workspace px-3 py-10 font-body sm:px-6 lg:px-10">
      <div
        className={cn(
          // `studio-doc`: raiz que mede a largura do documento. É o que faz o
          // modo celular do editor colapsar as colunas de verdade, mesmo com a
          // janela do navegador larga.
          'studio-doc studio-editor-document mx-auto min-h-[calc(100%-2rem)] overflow-hidden border border-border/70 shadow-[0_20px_60px_-24px_hsl(var(--foreground)/0.22),0_4px_16px_-8px_hsl(var(--foreground)/0.12)] transition-[max-width] duration-300 motion-reduce:transition-none',
        )}
        style={estiloDocumento}
      >
        {!blocks.length ? (
          <div className="flex min-h-[640px] items-center justify-center p-8 text-center">
            <div className="max-w-sm space-y-3">
              <p className="font-display text-base font-semibold">Comece escolhendo um bloco pronto</p>
              <p className="text-xs text-muted-foreground">
                Selecione uma categoria e insira o primeiro modelo neste documento.
              </p>
              {onRequestInsert && (
                <Button
                  type="button"
                  size="sm"
                  className="gap-1.5 bg-studio-accent text-studio-accent-foreground hover:bg-studio-accent/90"
                  onClick={() => onRequestInsert(0)}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Adicionar bloco
                </Button>
              )}
            </div>
          </div>
        ) : (
          <StudioWebProvider value={{ proposal: PROPOSTA_EXEMPLO, document, actions: {} }}>
            {/* Sem moldura: as faixas encostam nas laterais e uma na outra,
                igual à página publicada. */}
            <div>
              {blocks.map((node, indice) => {
                const definicao = getBlock(node.type);
                const selecionado = node.id === selectedId;

                return (
                  <React.Fragment key={node.id}>
                    {onRequestInsert && (
                      <PontoDeInsercao
                        indice={indice}
                        ativo={insertIndex === indice}
                        onClick={() => onRequestInsert(indice)}
                      />
                    )}

                    <section
                      role="button"
                      tabIndex={0}
                      aria-label={`Editar ${definicao?.label ?? node.type}`}
                      onClick={() => onSelect(node.id)}
                      onKeyDown={(evento) => {
                        if (evento.key === 'Enter' || evento.key === ' ') onSelect(node.id);
                      }}
                      className={cn(
                        'group/bloco relative cursor-pointer outline-none transition-shadow motion-reduce:transition-none',
                        selecionado
                          ? 'ring-2 ring-inset ring-studio-accent'
                          : 'hover:ring-1 hover:ring-inset hover:ring-studio-accent/60 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-studio-accent',
                      )}
                    >
                      <div className="pointer-events-none">
                        <StudioWebNode node={node} />
                      </div>

                      <div
                        className={cn(
                          'absolute left-2 top-2 flex items-center gap-0.5 rounded-md border bg-popover p-1 text-popover-foreground shadow-lg transition-opacity motion-reduce:transition-none',
                          selecionado
                            ? 'opacity-100'
                            : 'opacity-0 group-hover/bloco:opacity-100 group-focus-visible/bloco:opacity-100',
                        )}
                      >
                        <span className="flex items-center gap-1 px-1.5 text-[10px] font-medium">
                          <GripVertical className="h-3 w-3 text-muted-foreground" />
                          {definicao?.label ?? node.type}
                        </span>
                        <AcaoBloco rotulo="Mover para cima" disabled={indice === 0} onClick={() => onMove(node.id, -1)}>
                          <ArrowUp className="h-3.5 w-3.5" />
                        </AcaoBloco>
                        <AcaoBloco
                          rotulo="Mover para baixo"
                          disabled={indice === blocks.length - 1}
                          onClick={() => onMove(node.id, 1)}
                        >
                          <ArrowDown className="h-3.5 w-3.5" />
                        </AcaoBloco>
                        <AcaoBloco rotulo="Duplicar bloco" onClick={() => onDuplicate(node.id)}>
                          <Copy className="h-3.5 w-3.5" />
                        </AcaoBloco>
                        <AcaoBloco rotulo="Remover bloco" destructive onClick={() => onRemove(node.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </AcaoBloco>
                      </div>
                    </section>
                  </React.Fragment>
                );
              })}

              {onRequestInsert && (
                <PontoDeInsercao
                  indice={blocks.length}
                  ativo={insertIndex === blocks.length}
                  onClick={() => onRequestInsert(blocks.length)}
                />
              )}
            </div>
          </StudioWebProvider>
        )}
      </div>
    </div>
  );
}